import { supabase } from '../lib/supabase';
import { prepararSolicitudActividad, rechazoConfirmadoActividad, ResultadoActividadInciertoError, RPC_SOLICITUDES_ACTIVIDAD } from '../utils/solicitudActividad';
import type { SolicitudActividad } from '../utils/solicitudActividad';
import { comenzarOperacionActividad, conservarOperacionActividad, resolverOperacionActividad } from '../utils/operacionActividadPendiente';

export type EstadoSolicitudActividad<T> =
  | { readonly estado: 'confirmada'; readonly resultado: T }
  | { readonly estado: 'no_confirmada' };

interface ContextoSolicitudActividad {
  readonly actor: string;
  readonly vigente: () => boolean;
}

async function sesionParaSolicitud(contexto?: ContextoSolicitudActividad) {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (!session?.access_token) throw new Error('Inicia sesión para registrar la operación.');
  if (contexto && (!contexto.vigente() || session.user.id !== contexto.actor)) {
    throw new Error('La sesión cambió. Verifica la operación con su usuario original.');
  }
  return session;
}

export const solicitudActividadService = {
  ejecutarContrato<T>(operacion: string, argumentos: Readonly<Record<string, unknown>>,
    argumentosContrato: Readonly<Record<string, unknown>>): Promise<T> {
    const solicitud = prepararSolicitudActividad(RPC_SOLICITUDES_ACTIVIDAD[operacion], operacion,
      argumentos, argumentosContrato);
    return this.ejecutar<T>(solicitud);
  },
  async ejecutar<T>(solicitud: SolicitudActividad, contexto?: ContextoSolicitudActividad): Promise<T> {
    const session = await sesionParaSolicitud(contexto);
    const actor = session.user.id;
    const reintento = comenzarOperacionActividad(actor, solicitud);
    let respuesta: Awaited<ReturnType<typeof supabase.rpc>>;
    try {
      // La cabecera pertenece a este envío; un cambio posterior de sesión no
      // sustituye silenciosamente el actor del payload. Nunca se persiste el JWT.
      respuesta = await supabase.rpc(solicitud.rpc, { ...solicitud.argumentos, p_solicitud: solicitud.id })
        .setHeader('Authorization', `Bearer ${session.access_token}`);
    } catch (error: unknown) {
      conservarOperacionActividad(actor, solicitud);
      throw new ResultadoActividadInciertoError(solicitud, error);
    }
    if (respuesta.error) {
      // Rechazar un replay no demuestra que la solicitud original haya fallado:
      // por ejemplo, el actor pudo perder permisos después de que se confirmara.
      if (!reintento && rechazoConfirmadoActividad(respuesta.error)) {
        resolverOperacionActividad(actor, solicitud.id);
        throw respuesta.error;
      }
      conservarOperacionActividad(actor, solicitud);
      throw new ResultadoActividadInciertoError(solicitud, respuesta.error);
    }
    if (respuesta.data === null || respuesta.data === undefined) {
      conservarOperacionActividad(actor, solicitud);
      throw new ResultadoActividadInciertoError(solicitud, 'La RPC no devolvió resultado');
    }
    resolverOperacionActividad(actor, solicitud.id);
    return respuesta.data as T;
  },

  async consultar<T>(solicitud: SolicitudActividad, contexto?: ContextoSolicitudActividad): Promise<EstadoSolicitudActividad<T>> {
    const session = await sesionParaSolicitud(contexto);
    // Los argumentos del getter son el objeto exacto persistido por cada contrato
    // (id/datos o id/cambios). El adaptador de dominio los extrae del snapshot,
    // nunca del formulario que pudo cambiar después del envío.
    const { data, error } = await supabase.rpc('consultar_solicitud_actividad', {
      p_solicitud: solicitud.id, p_operacion: solicitud.operacion, p_argumentos: solicitud.argumentosContrato,
    }).setHeader('Authorization', `Bearer ${session.access_token}`);
    if (error) throw error;
    if (data?.estado === 'confirmada' && data.resultado !== null && data.resultado !== undefined) {
      return { estado: 'confirmada', resultado: data.resultado as T };
    }
    if (data?.estado === 'no_confirmada') return { estado: 'no_confirmada' };
    throw new ResultadoActividadInciertoError(solicitud, 'La consulta no devolvió un estado válido');
  },
};
